import React, { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import './WaitingRoom.css';
import ChatBox from './ChatBox';
import { useMutatePayToStart } from '../hooks/mutations';
import ProgressModal from './modals/ProgressModal';
import socketService from '../services/socketService';

const WaitingRoom = ({ socket, onBackToLobby }) => {
  const { isConnected } = useAccount();
  const [players, setPlayers] = useState([]);
  const [countdown, setCountdown] = useState(null);
  // Chat state removed - now handled by ChatBox component
  const [isReady, setIsReady] = useState(false);
  const [canStart, setCanStart] = useState(false);
  const [entryFee, setEntryFee] = useState('Loading...');
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');

  const { mutate: payToStart, isPending: isPayToStartLoading } = useMutatePayToStart();

  // Helper function to deduplicate players
  const deduplicatePlayers = (playerList) => {
    const seen = new Set();
    const uniquePlayers = [];

    for (const player of playerList) {
      // Create a unique identifier using socketId and username
      const uniqueKey = `${player.id}-${player.username}`;

      if (!seen.has(uniqueKey)) {
        seen.add(uniqueKey);
        uniquePlayers.push(player);
      }
    }

    return uniquePlayers;
  };

  // Validate player objects coming from the socket before they enter state
  const isValidPlayer = (player) => {
    if (!player || typeof player !== 'object') return false;

    const hasValidId =
      typeof player.id === 'string' &&
      player.id.trim() !== '';

    const hasValidUsername =
      typeof player.username === 'string' &&
      player.username.trim() !== '';

    return hasValidId && hasValidUsername;
  };

  const sanitizePlayers = (playerList) => {
    const safeList = Array.isArray(playerList) ? playerList : [];
    const filtered = safeList.filter(isValidPlayer);
    return deduplicatePlayers(filtered);
  };

  useEffect(() => {
    if (!socket) return;

    socket.on('playerListUpdate', (playerList) => {
      // Clean and deduplicate players to prevent invalid or duplicate entries
      const validPlayers = sanitizePlayers(playerList);

      setPlayers(validPlayers);

      // Check if current player is in the list and update ready status
      const currentPlayer = validPlayers.find((player) => player.id === socket.id);
      if (currentPlayer) {
        setIsReady(currentPlayer.ready);
      }
    });

    socket.on('countdownUpdate', (seconds) => {
      setCountdown(seconds);
    });

    socket.on('countdownCancel', () => {
      setCountdown(null);
      setCanStart(false);
    });

    socket.on('gameReset', ({ roomId }) => {
      // Reset UI state for new game
      setIsReady(false);
      setCanStart(false);
      setCountdown(null);
    });

    // Chat message handler removed - now handled by ChatBox component

    socket.on('readyStatusUpdate', ({ id, ready }) => {

      // Update the current player's ready status
      if (socket.id === id) {
        setIsReady(ready);
      }

      // Update the ready status in the players list for real-time display
      setPlayers((prevPlayers) => {
        const updatedPlayers = prevPlayers.map((player) =>
          player.id === id ? { ...player, ready } : player,
        );
        // Re-validate and deduplicate list after updates to keep state clean
        return sanitizePlayers(updatedPlayers);
      });
    });

    return () => {
      socket.off('playerListUpdate');
      socket.off('countdownUpdate');
      socket.off('countdownCancel');
      socket.off('gameReset');
      // socket.off('message') removed - handled by ChatBox
      socket.off('readyStatusUpdate');
    };
  }, [socket]);

  // Set entry fee and check wallet connection
  useEffect(() => {
    if (isConnected) {
      setEntryFee('0.001 tBNB (estimated)');
    } else {
      setEntryFee('Connect wallet to play');
    }
  }, [isConnected]);

  const handlePayToStart = async () => {
    setIsProgressModalOpen(true);
    payToStart({
      gameId: socket.id,
      username: localStorage.getItem('username'),
      depositFee: '1000000000000000000',
      platformFee: '100000000000000000',
      onProgress: ({ step, status }) => {
        setCurrentStep(step);
        setCurrentStatus(status);
      },
    }, {
      onSuccess: (res) => {
        console.log('🎮 Payment successful', res);
        setCanStart(true);
        setIsProgressModalOpen(false);
      },
      onError: (error) => {
        console.log('🎮 Payment failed:', error);
        setIsProgressModalOpen(false);
      },
    });
    // setCanStart(true);
  };

  const handleReady = () => {
    if (!canStart) {
      alert('Please complete payment first!');
      return;
    }

    const newReadyStatus = !isReady;
    socket.emit('playerReady', newReadyStatus);
  };

  // handleSendMessage removed - now handled by ChatBox component

  const playerCount = players.length;
  const maxPlayers = 4;
  const percentage = (playerCount / maxPlayers) * 100;

  return (
    <div className='min-h-screen flex items-center justify-center p-2 sm:p-4'>
      {/* Main Content Container */}
      <div className={`flex flex-col lg:flex-row gap-4 lg:gap-6 w-full ${canStart ? 'max-w-6xl' : 'max-w-4xl'}`}>
        {/* Left Panel - Game Status */}
        <div className='w-full max-w-[700px]'>
          <div
            className={`bg-gray-900/80 backdrop-blur-lg rounded-xl sm:rounded-2xl border border-gray-700/50 shadow-2xl transition-all duration-500 ease-in-out ${
              canStart ? 'p-4 sm:p-6 lg:p-8' : 'p-3 sm:p-4 lg:p-6'
            }`}>
            {/* Header */}
            <div
              className={`text-center mb-4 sm:mb-6 lg:mb-8 transition-all duration-500 ease-in-out ${
                canStart ? 'transform scale-105' : 'transform scale-100'
              }`}>
              <p
                className={`font-bold bg-gradient-to-r text-xl sm:text-2xl lg:text-3xl from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-2 transition-all duration-500 ease-in-out`}>
                {canStart ? 'Ready to Hunt!' : 'Waiting for Players...'}
              </p>
              <p className={`text-gray-300 transition-all duration-500 ease-in-out text-xs sm:text-sm lg:text-base`}>
                {canStart ? 'All systems ready for battle!' : 'Prepare for an epic Bomberman battle!'}
              </p>
            </div>

            {/* Payment Section */}
            {isConnected && !canStart && (
              <div className='mb-4 sm:mb-6 lg:mb-8 bg-gradient-to-r from-purple-900/40 to-blue-900/40 rounded-xl p-3 sm:p-4 lg:p-6 border border-purple-500/30 transition-all duration-500 ease-in-out animate-pulse'>
                <div className='text-center mb-3 sm:mb-4 lg:mb-6'>
                  <p className='text-lg sm:text-xl lg:text-2xl font-bold text-purple-300 mb-2'>🎮 Ready to Hunt?</p>
                  <p className='text-gray-300 text-xs sm:text-sm lg:text-base'>
                    Pay the entry fee to join this exciting battle!
                  </p>
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 lg:gap-4 mb-3 sm:mb-4 lg:mb-6'>
                  <div className='bg-black/30 rounded-lg p-2 sm:p-3 lg:p-4 text-center transition-all duration-300 hover:scale-105'>
                    <div className='text-emerald-400 font-bold text-sm sm:text-base lg:text-lg'>{entryFee}</div>
                    <div className='text-gray-400 text-xs sm:text-sm'>Entry Fee</div>
                  </div>
                  {/* <div className="bg-black/30 rounded-lg p-3 sm:p-4 text-center transition-all duration-300 hover:scale-105">
                    <div className="text-yellow-400 font-bold text-sm">Winner Takes All</div>
                    <div className="text-gray-400 text-xs sm:text-sm">Reward Pool</div>
                  </div> */}
                  <div className='bg-black/30 rounded-lg p-2 sm:p-3 lg:p-4 text-center transition-all duration-300 hover:scale-105 sm:col-span-2 lg:col-span-1'>
                    <div className='text-blue-400 font-bold text-sm sm:text-base lg:text-lg'>BSC Testnet</div>
                    <div className='text-gray-400 text-xs sm:text-sm'>Network</div>
                  </div>
                </div>

                <div className='flex flex-col sm:flex-row gap-2 sm:gap-3'>
                  <button
                    className={`flex-1 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-bold py-2 sm:py-3 px-3 sm:px-4 lg:px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 text-xs sm:text-sm lg:text-base ${
                      isPayToStartLoading || players?.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onClick={handlePayToStart}
                    
                    disabled={isPayToStartLoading || players?.length === 0}
                    >
                    <i className='fas fa-credit-card'></i>
                    {isPayToStartLoading ? 'Processing...' : 'Pay Entry Fee'}
                  </button>
                </div>
              </div>
            )}

            {/* Wallet Not Connected Section */}
            {!isConnected && (
              <div className='mb-4 sm:mb-6 lg:mb-8 bg-gradient-to-r from-red-900/40 to-orange-900/40 rounded-xl p-3 sm:p-4 lg:p-6 border border-red-500/30 text-center transition-all duration-500 ease-in-out'>
                <h3 className='text-base sm:text-lg lg:text-xl font-bold text-red-300 mb-2'>⚠️ Wallet Required</h3>
                <p className='text-gray-300 mb-3 sm:mb-4 text-xs sm:text-sm lg:text-base'>
                  Connect your wallet in the lobby to join this game.
                </p>
                <button
                  className='bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold py-2 sm:py-3 px-3 sm:px-4 lg:px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 mx-auto text-xs sm:text-sm lg:text-base'
                  onClick={onBackToLobby}>
                  <i className='fas fa-arrow-left'></i>
                  Back to Lobby
                </button>
              </div>
            )}

            {/* Players Section */}
            <div
              className={`mb-4 sm:mb-6 lg:mb-8 transition-all duration-500 ease-in-out ${
                canStart ? 'transform scale-105' : 'transform scale-100'
              }`}>
              <h3
                className={`font-bold text-white mb-2 sm:mb-3 lg:mb-4 flex items-center gap-2 transition-all duration-500 ease-in-out text-sm sm:text-base lg:text-lg ${
                  canStart ? 'lg:text-xl' : ''
                }`}>
                <i className='fas fa-users text-cyan-400'></i>
                Connected Players ({playerCount}/{maxPlayers})
              </h3>

              {/* Progress Bar */}
              <div className='mb-3 sm:mb-4 lg:mb-6'>
                <div className='bg-gray-700 rounded-full h-2 sm:h-3 overflow-hidden'>
                  <div
                    className='bg-gradient-to-r from-emerald-500 to-cyan-500 h-full transition-all duration-700 ease-out'
                    style={{ width: `${percentage}%` }}></div>
                </div>
                <p className='text-center text-gray-400 text-xs sm:text-sm mt-1 sm:mt-2'>
                  {playerCount < 2 ? 'Waiting for more players...' : 'Ready to start!'}
                </p>
              </div>

              {/* Players List */}
              <div className='space-y-1 sm:space-y-2 lg:space-y-3'>
                {players.length === 0 ? (
                  <div className='text-center py-4 sm:py-6 lg:py-8 text-gray-400'>
                    <i className='fas fa-clock text-xl sm:text-2xl lg:text-3xl mb-2 sm:mb-3'></i>
                    <p className='text-xs sm:text-sm lg:text-base'>No players connected yet</p>
                  </div>
                ) : (
                  players.map((player, index) => (
                    <div
                      key={`player-${player.id || 'unknown'}-${player.username || 'anonymous'}-${index}`}
                      className={`flex items-center justify-between p-2 sm:p-3 lg:p-4 rounded-lg border transition-all duration-300 hover:scale-105 ${
                        player.ready ? 'bg-emerald-900/30 border-emerald-500/50' : 'bg-gray-800/50 border-gray-600/50'
                      }`}>
                      <div className='flex items-center gap-2 sm:gap-3 min-w-0 flex-1'>
                        <div
                          className={`w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0 ${
                            player.ready ? 'bg-emerald-500 text-white' : 'bg-gray-600 text-gray-300'
                          }`}>
                          {(player.username || `P${index + 1}`).charAt(0).toUpperCase()}
                        </div>
                        <div className='min-w-0 flex-1'>
                          <span className='text-white font-medium text-xs sm:text-sm lg:text-base truncate block'>
                            {player.username || `Player ${index + 1}`}
                          </span>
                          {player.id === socket?.id && <span className='text-xs text-cyan-400'>(You)</span>}
                        </div>
                      </div>
                      <div
                        className={`flex items-center gap-1 sm:gap-2 px-1 sm:px-2 lg:px-3 py-1 rounded-full text-xs sm:text-sm font-medium flex-shrink-0 ${
                          player.ready ? 'bg-emerald-500/20 text-emerald-300' : 'bg-yellow-500/20 text-yellow-300'
                        }`}>
                        <i className={`fas ${player.ready ? 'fa-check' : 'fa-clock'} text-xs`}></i>
                        <span className='hidden sm:inline'>{player.ready ? 'Ready' : 'Waiting'}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Game Controls */}
            <div
              className={`flex flex-col sm:flex-row gap-2 sm:gap-3 lg:gap-4 transition-all duration-500 ease-in-out ${
                canStart ? 'transform scale-105' : 'transform scale-100'
              }`}>
              <button
                className={`flex-1 font-bold py-2 sm:py-3 lg:py-4 px-3 sm:px-4 lg:px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 text-xs sm:text-sm lg:text-base ${
                  isReady
                    ? 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white animate-pulse'
                    : canStart
                    ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
                onClick={handleReady}
                disabled={!canStart}
                title={!canStart ? 'Complete payment first' : ''}>
                <i className={`fas ${isReady ? 'fa-times' : 'fa-play'} text-xs sm:text-sm`}></i>
                <span className='hidden sm:inline'>
                  {isReady ? 'Cancel Ready' : canStart ? 'Start the Hunt' : 'Pay to Start First'}
                </span>
                <span className='sm:hidden'>{isReady ? 'Cancel' : canStart ? 'Start' : 'Pay First'}</span>
              </button>

              <button
                className='bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 sm:py-3 lg:py-4 px-3 sm:px-4 lg:px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-xs sm:text-sm lg:text-base'
                onClick={onBackToLobby}>
                <i className='fas fa-arrow-left text-xs sm:text-sm'></i>
                <span className='hidden sm:inline'>Back to Lobby</span>
                <span className='sm:hidden'>Back</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Panel - Chat (hidden on mobile, shown on desktop like Game.jsx) */}
        <div className='hidden lg:block flex-1 max-w-md space-y-6'>
          {/* Chat Panel */}
          <div className='h-fit'>
            <ChatBox
              socket={socket}
              className={'h-full min-h-[440px]'}
              initialMessages={[
                'Welcome to the Bomberman waiting room! Chat with other players while you wait for the game to start.',
              ]}
            />
          </div>
        </div>
      </div>

      {/* Countdown Overlay */}
      {countdown !== null && (
        <div className='fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4'>
          <div className='text-center animate-bounce'>
            <h2 className='text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-white mb-3 sm:mb-4 lg:mb-6'>
              Game Starting In
            </h2>
            <div className='text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent animate-pulse'>
              {countdown}
            </div>
            <p className='text-base sm:text-lg lg:text-xl text-gray-300 mt-3 sm:mt-4'>Get ready to hunt!</p>
          </div>
        </div>
      )}
      <ProgressModal
        show={isProgressModalOpen}
        onClose={() => setIsProgressModalOpen(false)}
        currentStep={currentStep}
        status={currentStatus}
        progress={currentStep === 'approve' ? 25 : currentStep === 'payment' ? 75 : 0}
      />
    </div>
  );
};

export default WaitingRoom;
